import styled from "styled-components";

import { futureMoneyIconRound, philBarrar } from "@assets/images";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { EntryStepLayout } from "@entry/EntryStepLayout";
import { entryColors, entryFont } from "@entry/tokens";
import { useOnboarding, useUser } from "@hooks";
import { Translations, i18n } from "@i18n";

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: auto;
`;

const AppIcon = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 32px;
`;

const Headline = styled.h2`
  margin: 20px 0 0;
  font-family: ${entryFont.title};
  font-size: 24px;
  line-height: 30px;
  font-weight: 700;
  text-align: center;
  color: #ffac7d;
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  font-family: ${entryFont.title};
  font-size: 24px;
  line-height: 30px;
  font-weight: 400;
  text-align: center;
  color: ${entryColors.textPrimary};
`;

const Body = styled.p`
  margin: 0;
  font-family: ${entryFont.body};
  font-size: 14px;
  line-height: 1.55;
  color: ${entryColors.textSecondary};
`;

const BodySection = styled.div`
  margin-top: 28px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  text-align: left;
  width: 100%;
`;

const AuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 24px;
  align-self: flex-start;
`;

const AuthorAvatar = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 22px;
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const AuthorName = styled.span`
  font-family: ${entryFont.body};
  font-size: 14px;
  font-weight: 500;
  color: ${entryColors.textPrimary};
`;

const AuthorTitle = styled.span`
  font-family: ${entryFont.body};
  font-size: 14px;
  color: ${entryColors.textPrimary};
`;

export const SecureAccountScreen = () => {
  const { user } = useUser();
  const { resetToNext } = useOnboarding();

  const preferredName = user?.preferredName || user?.firstName;

  const handleContinue = () => {
    sessionStorage.setItem("hasSeenSecureAccountIntro", "true");
    resetToNext();
  };

  const title = preferredName
    ? i18n.t(Translations.EntrySecureAccountTitle, { preferredName })
    : i18n.t(Translations.EntrySecureAccountTitleNoName);

  const props: OnboardingLayoutProps = {
    cta: {
      primary: {
        title: i18n.t(Translations.EntrySecureAccountCTA),
        onClick: handleContinue,
      },
    },
  };

  return (
    <EntryStepLayout {...props}>
      <CardContent>
        <AppIcon src={futureMoneyIconRound} alt="FutureMoney" />
        <Headline>{title}</Headline>
        <Subtitle>{i18n.t(Translations.EntrySecureAccountSubtitle)}</Subtitle>
        <BodySection>
          <Body>{i18n.t(Translations.EntrySecureAccountBody1)}</Body>
          <Body>{i18n.t(Translations.EntrySecureAccountBody2)}</Body>
        </BodySection>
        <AuthorRow>
          <AuthorAvatar src={philBarrar} alt={i18n.t(Translations.EntrySecureAccountAuthorName)} />
          <AuthorInfo>
            <AuthorName>{i18n.t(Translations.EntrySecureAccountAuthorName)}</AuthorName>
            <AuthorTitle>{i18n.t(Translations.EntrySecureAccountAuthorTitle)}</AuthorTitle>
          </AuthorInfo>
        </AuthorRow>
      </CardContent>
    </EntryStepLayout>
  );
};
